import { Bookmark, Chat, Heart, InstagramLogo } from '@phosphor-icons/react/dist/ssr';

import { appendProtocol } from '@/postContentElement/components/utils/link';
import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

interface Props {
  anchorId?: string;
  postUrl?: string;
  authorProfileImage?: string;
  authorName?: string;
  displayFollowerCount?: boolean;
  followersDisplay?: string;
  thumbnailUrl?: string;
  description?: string;
  logoImageUrl?: string;
  screenshotUrl?: string;
  screenshotFramed?: boolean;
}

const Instagram = ({
  anchorId,
  postUrl,
  authorProfileImage,
  authorName,
  displayFollowerCount,
  followersDisplay,
  thumbnailUrl,
  description,
  screenshotUrl,
  screenshotFramed,
}: Props) => {
  const { utmParams } = usePostContentContext();
  const postUrlWithUTMParams = evaluateAndAppendUTMParams(appendProtocol(postUrl), utmParams);

  return (
    <div
      id={anchorId}
      className="bh__ig_wrapper"
      style={{
        padding: '0.5rem 0.25rem',
      }}
    >
      {/* Framed screenshots already include the card border and its 4px corner radius. */}
      <blockquote
        className={`bh__ig_media mx-auto p-0${screenshotFramed ? '' : ' border border-[#d1d5db] bg-white hover:bg-[#f9fafb]'}`}
        style={{
          maxWidth: '420px',
          minWidth: '326px',
          width: 'calc(100% - 2px)',
          borderRadius: screenshotFramed ? undefined : '0.5rem',
        }}
      >
        <div>
          {screenshotUrl ? (
            <a
              href={postUrlWithUTMParams}
              className="p-0 text-center w-full no-underline"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={screenshotUrl}
                alt="Instagram post"
                className="block w-full h-auto"
                // Unframed: the 0.5rem card corner minus its 1px border, so the image corners stay inside it.
                style={{ borderRadius: screenshotFramed ? '4px' : 'calc(0.5rem - 1px)' }}
              />
            </a>
          ) : (
            <a href={postUrlWithUTMParams} target="_blank" className="p-0 text-center no-underline	" rel="noreferrer">
              <div className="flex items-center" style={{ padding: '0.75rem' }}>
                {authorProfileImage && (
                  <div className="grow-0" style={{ marginRight: '0.75rem' }}>
                    <img
                      src={authorProfileImage}
                      className="w-[40px] h-[40px] border-0 block rounded-lg"
                      alt={authorName}
                    />
                  </div>
                )}
                <div>
                  <p className="text-left" style={{ lineHeight: 1.5, color: 'rgba(31,41,55,1)' }}>
                    <strong>{authorName}</strong>
                  </p>
                  {displayFollowerCount && (
                    <p
                      className="text-left"
                      style={{ lineHeight: 1.5, fontSize: '0.875rem', color: 'rgba(107,114,128,1)' }}
                    >
                      {followersDisplay}
                    </p>
                  )}
                </div>
              </div>
              <div className="bh__ig_image_wrapper relative border-b border-t border-[#d1d5db]">
                <img alt={description} src={thumbnailUrl} className="block h-auto max-w-full border-0" />
              </div>
              <div style={{ padding: '0.75rem' }}>
                <p className="text-[#0095f6] text-center" style={{ lineHeight: 1.5 }}>
                  <strong>View more on Instagram</strong>
                </p>
              </div>

              <div style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                <div className="border-t border-[#d1d5db] w-full" />
                <div className="flex items-center" style={{ paddingTop: '0.5rem', paddingBottom: '1rem' }}>
                  {/* Heart */}
                  <Heart size={14} />

                  {/* Comment Bubble */}
                  <Chat size={14} />

                  {/* Bookmark */}
                  <Bookmark size={14} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <p className="text-left color-[#1f2937]">
                    <strong>{authorName}</strong>
                  </p>
                  {description && (
                    <div
                      className="text-left color-[#1f2937] whitespace-pre-line"
                      style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
                    >
                      {description}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-[#d1d5db] w-full" />
              <div className="flex justify-end items-center" style={{ padding: '0.75rem' }}>
                <div>
                  <InstagramLogo size={20} />
                </div>
              </div>
            </a>
          )}
        </div>
      </blockquote>
    </div>
  );
};

export { Instagram };
